import React, { useEffect, useMemo, useState } from "react";
import { orderBy } from "lodash";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import { PROJECTS_NAMES_QUERY } from "../../constants/graphql/queries";
import useAuth from "../../hooks/useAuth";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel as getProjectClientLabelFromHelper,
  getProjectOptionLabel as getProjectOptionLabelFromHelper,
} from "../../utils/projectOptionLabels";

export default function SelectProjectModal({ onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [open, setOpen] = useState(true);
  const { data, loading } = useQuery(PROJECTS_NAMES_QUERY);
  const label = `Pick a ${settings.filter_field2} to Enter the Store`;
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);

  const projects = useMemo(() => {
    if (!data?.projects) return [];
    return orderBy(data.projects, [project => project.name?.toLowerCase(), "id"], ["asc", "asc"]);
  }, [data?.projects]);

  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projects),
    [projects]
  );

  const getProjectClientLabel = option => {
    return getProjectClientLabelFromHelper(
      option,
      duplicateNameCounts,
      showClientNameInProjectList
    );
  };

  const getProjectOptionLabel = option => {
    return getProjectOptionLabelFromHelper(
      option,
      duplicateNameCounts,
      showClientNameInProjectList
    );
  };

  useEffect(() => {
    if (!loading && projects.length === 1) {
      navigate(`/shopping/${projects[0].id}`);
    }
  }, [loading, navigate, projects]);

  const handleViewInventory = () => {
    navigate(`/shopping/${selectedProject.id}`);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box width={40} />
          <Typography fontSize={22} fontWeight={500} align="center" color="text.primary">
            {label}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              fullWidth
              options={projects}
              getOptionLabel={option => getProjectOptionLabel(option)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={
                (props, option) => {
                  const optionProps = { ...props };
                  delete optionProps.key;
                  const clientLabel = getProjectClientLabel(option);
                  return (
                    <li {...optionProps} key={option.id}>
                      {option.name}
                      {clientLabel ? (
                        <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                          {" - "}
                          {clientLabel}
                        </Typography>
                      ) : null}
                    </li>
                  );
                }
              }
              value={selectedProject}
              onChange={(event, newValue) => {
                setSelectedProject(newValue || null);
                if (!newValue) return;
                navigate(`/shopping/${newValue.id}`);
              }}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={`Select a ${settings.filter_field2}`}
                  variant="outlined"
                  autoFocus
                />
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box p={2} display="flex" justifyContent="flex-end" width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewInventory}
            disabled={!selectedProject}
          >
            Go to Store
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
